import React, { useState, useEffect } from 'react';
import { Calculator, BarChart3, DollarSign, Building2, FileBarChart } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { formatCurrency } from '../../utils/formatters';

const CostAccountingDashboard = () => {
  const [productionCosts, setProductionCosts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [chartOfAccounts, setChartOfAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch production costs
      const { data: productionCostsData, error: costsError } = await supabase
        .from('production_costs')
        .select('*');

      if (costsError) throw costsError;

      // Fetch employees (for department-based cost centers)
      const { data: employeesData, error: employeesError } = await supabase
        .from('employees')
        .select('*');

      if (employeesError) throw employeesError;

      // Fetch chart of accounts (for cost elements)
      const { data: accountsData, error: accountsError } = await supabase
        .from('chart_of_accounts')
        .select('*');

      if (accountsError) throw accountsError;

      setProductionCosts(productionCostsData || []);
      setEmployees(employeesData || []);
      setChartOfAccounts(accountsData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
        <p className="text-gray-600">載入中...</p>
      </div>
    );
  }

  // Calculate summary statistics
  const totalDepartments = [...new Set(employees.map(emp => emp.department).filter(Boolean))].length;
  const totalCostAccounts = chartOfAccounts.filter(account => 
    account.account_type === 'EXPENSE' || account.account_name.includes('Cost')
  ).length;
  const totalProduction = productionCosts.reduce((sum, item) => sum + (item.amount || 0), 0);
  const totalProductionItems = productionCosts.length;
  const avgUnitCost = totalProductionItems > 0 ? totalProduction / totalProductionItems : 0;
  
  // Calculate variance statistics (simplified since production_costs table structure is different)
  const costVariance = productionCosts.reduce((sum, item) => {
    // Assuming variance can be calculated as difference between actual and expected cost
    const variance = (item.amount || 0) - (item.amount * 0.95 || 0); // Simple 5% variance assumption
    return sum + Math.abs(variance);
  }, 0);
  
  const totalPositiveVariance = costVariance * 0.6; // Assume 60% is over-budget
  const totalNegativeVariance = costVariance * 0.4; // Assume 40% is under-budget

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">成本會計儀表板</h2>
        <p className="text-gray-600">Cost Accounting Dashboard</p>
      </div>
      
      {/* Dashboard header image */}
      <div className="relative h-48 mb-6 overflow-hidden rounded-lg shadow-md">
        <img 
          src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2011&q=80" 
          alt="Cost Accounting Dashboard" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
          <h1 className="text-white text-3xl font-bold">成本會計系統</h1>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-700">總部門數</h3>
            <Building2 className="h-6 w-6 text-blue-500" />
          </div>
          <p className="text-3xl font-bold text-gray-800">{totalDepartments}</p>
          <p className="text-sm text-gray-500 mt-2">活躍的部門數量</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-700">總生產成本</h3>
            <DollarSign className="h-6 w-6 text-green-500" />
          </div>
          <p className="text-3xl font-bold text-gray-800">{formatCurrency(totalProduction)}</p>
          <p className="text-sm text-gray-500 mt-2">本期生產總成本</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-700">平均單位成本</h3>
            <Calculator className="h-6 w-6 text-purple-500" />
          </div>
          <p className="text-3xl font-bold text-gray-800">{formatCurrency(avgUnitCost)}</p>
          <p className="text-sm text-gray-500 mt-2">所有產品平均</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-700">成本差異</h3>
            <BarChart3 className="h-6 w-6 text-red-500" />
          </div>
          <div className="flex justify-between">
            <div>
              <p className="text-sm font-medium text-red-500">超支</p>
              <p className="text-xl font-bold text-red-500">{formatCurrency(totalPositiveVariance)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-green-500">節約</p>
              <p className="text-xl font-bold text-green-500">{formatCurrency(Math.abs(totalNegativeVariance))}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Production Costs */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-gray-800">最近生產成本</h3>
          <FileBarChart className="h-5 w-5 text-gray-500" />
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">產品</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">數量</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">總成本</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">單位成本</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">標準成本</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">差異</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {productionCosts.slice(0, 10).map((cost) => {
                const unitCost = cost.amount || 0;
                const standardCost = unitCost * 0.95; // Assume standard is 5% less
                const variance = unitCost - standardCost;
                const variancePercentage = standardCost > 0 ? (variance / standardCost) * 100 : 0;
                
                return (
                  <tr key={cost.cost_id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{cost.cost_category || 'Production Cost'}</div>
                      <div className="text-sm text-gray-500">{cost.cost_type || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">1</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatCurrency(cost.amount)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatCurrency(unitCost)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatCurrency(standardCost)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${variance > 0 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                        {variance > 0 ? '+' : ''}{formatCurrency(variance)} ({variancePercentage.toFixed(1)}%)
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cost Distribution Chart */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">成本分佈</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-md font-medium text-gray-700 mb-3">部門類型分佈</h4>
            <div className="space-y-4">
              {['Sales', 'Production', 'Administration', 'Finance'].map(dept => {
                const count = employees.filter(emp => emp.department === dept).length;
                const percentage = totalDepartments > 0 ? (count / employees.length) * 100 : 0;
                return (
                  <div key={dept} className="relative pt-1">
                    <div className="flex mb-2 items-center justify-between">
                      <div>
                        <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full bg-blue-100 text-blue-800">
                          {dept}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-semibold inline-block text-blue-800">
                          {percentage.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                    <div className="overflow-hidden h-2 mb-1 text-xs flex rounded bg-gray-200">
                      <div style={{ width: `${Math.min(percentage, 100)}%` }} 
                           className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500">
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">{count} 員工</div>
                  </div>
                );
              })}
            </div>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-md font-medium text-gray-700 mb-3">會計科目類型分佈</h4>
            <div className="space-y-4">
              {['ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE'].map(type => {
                const count = chartOfAccounts.filter(account => account.account_type === type).length;
                const percentage = totalCostAccounts > 0 ? (count / chartOfAccounts.length) * 100 : 0;
                return (
                  <div key={type} className="relative pt-1">
                    <div className="flex mb-2 items-center justify-between">
                      <div>
                        <span className={`text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full ${
                          type === 'ASSET' ? 'bg-green-100 text-green-800' : 
                          type === 'LIABILITY' ? 'bg-yellow-100 text-yellow-800' : 
                          type === 'EXPENSE' ? 'bg-purple-100 text-purple-800' : 
                          'bg-gray-100 text-gray-800'}`}>
                          {type}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-semibold inline-block text-gray-800">
                          {percentage.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                    <div className="overflow-hidden h-2 mb-1 text-xs flex rounded bg-gray-200">
                      <div style={{ width: `${Math.min(percentage, 100)}%` }} 
                           className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${
                             type === 'ASSET' ? 'bg-green-500' : 
                             type === 'LIABILITY' ? 'bg-yellow-500' : 
                             type === 'EXPENSE' ? 'bg-purple-500' : 
                             'bg-gray-500'}`}>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">{count} 科目</div>
                  </div>
                );
              })}
            </div>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-md font-medium text-gray-700 mb-3">生產成本類型分佈</h4>
            <div className="space-y-4">
              {[
                { name: '材料成本', category: 'Material' },
                { name: '人工成本', category: 'Labor' },
                { name: '製造費用', category: 'Overhead' }
              ].map(category => {
                const total = productionCosts
                  .filter(cost => cost.cost_category === category.category)
                  .reduce((sum, item) => sum + (item.amount || 0), 0);
                const percentage = totalProduction > 0 ? (total / totalProduction) * 100 : 0;
                return (
                  <div key={category.category} className="relative pt-1">
                    <div className="flex mb-2 items-center justify-between">
                      <div>
                        <span className={`text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full ${
                          category.category === 'Material' ? 'bg-green-100 text-green-800' : 
                          category.category === 'Labor' ? 'bg-yellow-100 text-yellow-800' : 
                          'bg-purple-100 text-purple-800'}`}>
                          {category.name}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-semibold inline-block text-gray-800">
                          {percentage.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                    <div className="overflow-hidden h-2 mb-1 text-xs flex rounded bg-gray-200">
                      <div style={{ width: `${Math.min(percentage, 100)}%` }} 
                           className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${
                             category.category === 'Material' ? 'bg-green-500' : 
                             category.category === 'Labor' ? 'bg-yellow-500' : 
                             'bg-purple-500'}`}>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">{formatCurrency(total)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CostAccountingDashboard;
